import { type CSSProperties, type ReactNode } from 'react';

import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { FieldHelp } from '../FieldHelp';
import { Icon } from '../Icon';
import { Pagination, type PaginationSize, type PaginationVariant, type PaginationProps } from '../Pagination';
import { Popover } from '../Popover';
import { Tooltip } from '../Tooltip';
import { HorizontalScrollbar } from './HorizontalScrollbar';
import styles from './Table.module.css';
import { emptyHorizontalScrollMetrics, readHorizontalScrollMetrics, useHorizontalScroll, type HorizontalScrollMetrics } from './useHorizontalScroll';

export interface TableFilterOption {
  label: string;
  value: string;
}

export type TableSortOrder = 'asc' | 'desc';

/** 表格内置分页保持受控；`rows` 传入完整、已筛选和排序后的数据集。 */
export interface TablePagination extends Pick<PaginationProps, 'align' | 'disabled' | 'hideOnSinglePage' | 'itemRender' | 'onPageSizeChange' | 'onShowSizeChange' | 'pageSizeOptions' | 'showLessItems' | 'showQuickJumper' | 'showSizeChanger' | 'showTitle' | 'showTotal' | 'simple' | 'totalBoundaryShowSizeChanger'> {
  ariaLabel?: string;
  /** 分页栏左侧附加信息，例如当前筛选结果总数。 */
  footerStart?: ReactNode;
  onPageChange: (page: number) => void;
  page: number;
  pageSize: number;
  size?: PaginationSize;
  variant?: PaginationVariant;
}

export interface TableColumn<Row> {
  align?: 'left' | 'center' | 'right';
  /** 二级表头：有子列时本列只做分组标题（可带 `help`），数据单元格按子列渲染。 */
  children?: readonly TableColumn<Row>[];
  className?: string;
  ellipsis?: boolean;
  /** 开启后表头显示筛选入口；需配合 `filterOptions` 与 `filterValues` 受控使用。 */
  filterable?: boolean;
  filterOptions?: readonly TableFilterOption[];
  /** 横向滚动时固定在表格左侧或右侧；固定列应声明明确宽度。 */
  fixed?: 'left' | 'right';
  /** Figma `Question` 槽位：表头问号提示，悬停展开说明。 */
  help?: string;
  key: string;
  minWidth?: number | string;
  /** 自定义单元格渲染；缺省时按 `row[key]` 输出文本。用于塞入 Tag / Switch / Button 等已有组件。 */
  render?: (row: Row, rowIndex: number) => ReactNode;
  /** 开启后表头可点击排序，需配合 `sortKey` / `sortOrder` / `onSortChange` 受控使用。 */
  sortable?: boolean;
  title: ReactNode;
  width?: number | string;
}

export interface TableProps<Row> {
  ariaLabel?: string;
  bordered?: boolean;
  className?: string;
  columns: readonly TableColumn<Row>[];
  emptyText?: string;
  filterValues?: Record<string, readonly string[]>;
  currentKey?: number | string;
  loading?: boolean;
  /** 开启后在最前方插入多选列，配合 `selectedKeys` / `onSelectionChange` 受控使用。 */
  selectable?: boolean;
  selectedKeys?: readonly (number | string)[];
  onFilterChange?: (key: string, values: readonly string[]) => void;
  onCurrentChange?: (key: number | string) => void;
  onSelectionChange?: (keys: readonly (number | string)[]) => void;
  onRowClick?: (row: Row) => void;
  onSortChange?: (key: string, order: TableSortOrder) => void;
  pagination?: TablePagination;
  rowKey: (row: Row) => number | string;
  /** 逐行附加属性（如引导用的 `data-tour` 锚点）；返回 undefined 表示这行不加。 */
  rowProps?: (row: Row) => Record<string, string | undefined> | undefined;
  rows: readonly Row[];
  scroll?: { x?: number; y?: number };
  sortKey?: string;
  sortOrder?: TableSortOrder;
  stripe?: boolean;
  stickyHeader?: boolean;
  tableLayout?: 'auto' | 'fixed';
  summary?: (rows: readonly Row[]) => readonly ReactNode[];
}

/** 表格的横向度量：在通用三项（clientWidth / scrollLeft / scrollWidth）之上补固定列宽度，固定区阴影按它算。 */
interface TableHorizontalScrollMetrics extends HorizontalScrollMetrics {
  fixedLeftWidths: Record<string, number>;
  fixedRightWidths: Record<string, number>;
}

const emptyTableHorizontalScrollMetrics: TableHorizontalScrollMetrics = { ...emptyHorizontalScrollMetrics, fixedLeftWidths: {}, fixedRightWidths: {} };

function readFixedColumnWidths(element: HTMLDivElement, side: 'left' | 'right'): Record<string, number> {
  return Object.fromEntries(
    Array.from(element.querySelectorAll<HTMLElement>(`thead [data-fixed="${side}"][data-column-key]`))
      .map((cell) => [cell.dataset.columnKey!, cell.getBoundingClientRect().width] as const)
      .filter(([, width]) => width > 0),
  );
}

function readTableHorizontalScrollMetrics(element: HTMLDivElement): TableHorizontalScrollMetrics {
  return {
    ...readHorizontalScrollMetrics(element),
    fixedLeftWidths: readFixedColumnWidths(element, 'left'),
    fixedRightWidths: readFixedColumnWidths(element, 'right'),
  };
}

function columnStyle<Row>(column: TableColumn<Row>, offset?: number): CSSProperties | undefined {
  if (column.width === undefined && column.minWidth === undefined && column.fixed === undefined) return undefined;
  const width = column.width === undefined ? undefined : typeof column.width === 'number' ? `${column.width}px` : column.width;
  const minWidth = column.minWidth === undefined ? undefined : typeof column.minWidth === 'number' ? `${column.minWidth}px` : column.minWidth;
  return { minWidth, width, [column.fixed ?? 'left']: column.fixed ? offset ?? 0 : undefined };
}

/** 真正渲染单元格的列：分组列展开成它的子列，普通列就是自己。 */
function leafColumnsOf<Row>(columns: readonly TableColumn<Row>[]): readonly TableColumn<Row>[] {
  return columns.flatMap((column) => (column.children?.length ? column.children : [column]));
}

function fixedColumnWidth<Row>(column: TableColumn<Row>, measuredWidths: Record<string, number>): number {
  return measuredWidths[column.key] ?? (typeof column.width === 'number' ? column.width : 0);
}

function getFixedOffsets<Row>(columns: readonly TableColumn<Row>[], measuredWidths: Record<string, number>) {
  const offsets = new Map<string, number>();
  let left = 0;
  for (const column of columns) if (column.fixed === 'left') { offsets.set(column.key, left); left += fixedColumnWidth(column, measuredWidths); }
  let right = 0;
  for (const column of [...columns].reverse()) if (column.fixed === 'right') { offsets.set(column.key, right); right += fixedColumnWidth(column, measuredWidths); }
  return offsets;
}

function cellContent<Row>(column: TableColumn<Row>, row: Row, rowIndex: number) {
  const content = column.render ? column.render(row, rowIndex) : String((row as Record<string, unknown>)[column.key] ?? '');
  return column.ellipsis && typeof content === 'string'
    ? <Tooltip content={content}><span className={styles.ellipsis} tabIndex={0}>{content}</span></Tooltip>
    : content;
}

function fixedOffset<Row>(columns: readonly TableColumn<Row>[], side: 'left' | 'right', measuredWidths: Record<string, number>): number {
  return columns
    .filter((column) => column.fixed === side)
    .reduce((total, column) => total + fixedColumnWidth(column, measuredWidths), 0);
}

function declaredTableMinWidth<Row>(columns: readonly TableColumn<Row>[], selectable: boolean): number | undefined {
  const widths = columns.map((column) => column.width);
  if (!widths.every((width) => typeof width === 'number')) return undefined;
  return widths.reduce<number>((total, width) => total + (width as number), selectable ? 38 : 0);
}

export function Table<Row>({ ariaLabel, bordered = true, className, columns, currentKey, emptyText = '暂无数据', filterValues = {}, loading = false, onCurrentChange, onFilterChange, onRowClick, onSortChange, pagination, rowKey, rowProps, rows, scroll, selectable = false, selectedKeys = [], onSelectionChange, sortKey, sortOrder = 'asc', stickyHeader = false, stripe = false, summary, tableLayout = 'auto' }: TableProps<Row>) {
  // 横向滚动（度量、拖动、Shift + 滚轮）与横条渲染都走共享实现，订单预览抽屉用的是同一份。
  const { ref: scrollRef, maxScrollLeft, metrics: horizontalScroll, onScroll, onWheel, setScrollLeft } = useHorizontalScroll(
    readTableHorizontalScrollMetrics,
    emptyTableHorizontalScrollMetrics,
    [columns, rows],
  );
  const pageCount = pagination ? Math.max(1, Math.ceil(rows.length / pagination.pageSize)) : 1;
  const visiblePage = pagination ? Math.min(Math.max(1, pagination.page), pageCount) : 1;
  const visibleRows = pagination
    ? rows.slice((visiblePage - 1) * pagination.pageSize, visiblePage * pagination.pageSize)
    : rows;
  const keys = visibleRows.map(rowKey);
  const selectedVisible = keys.filter((key) => selectedKeys.includes(key));
  const allSelected = keys.length > 0 && selectedVisible.length === keys.length;
  const toggleAll = () => onSelectionChange?.(allSelected ? [] : keys);
  const toggleRow = (key: number | string, checked: boolean) => onSelectionChange?.(checked ? [...selectedKeys, key] : selectedKeys.filter((item) => item !== key));

  /* 有二级表头时，宽度、固定列与单元格都按展开后的子列计算。 */
  const leafColumns = leafColumnsOf(columns);
  const hasGroupedHeader = columns.some((column) => column.children?.length);
  const hasHorizontalOverflow = maxScrollLeft > 0;
  const hasFixedLeft = leafColumns.some((column) => column.fixed === 'left');
  const hasFixedRight = leafColumns.some((column) => column.fixed === 'right');
  const fixedLeftOffset = fixedOffset(leafColumns, 'left', horizontalScroll.fixedLeftWidths);
  const fixedRightOffset = fixedOffset(leafColumns, 'right', horizontalScroll.fixedRightWidths);
  const tableMinWidth = declaredTableMinWidth(leafColumns, selectable);
  const fixedOffsets = getFixedOffsets(leafColumns, { ...horizontalScroll.fixedLeftWidths, ...horizontalScroll.fixedRightWidths });
  // 与 Ant 的 ping 状态一致：仅当该侧仍有内容被固定列遮挡时绘制阴影。
  const showFixedLeftShadow = hasHorizontalOverflow && hasFixedLeft && horizontalScroll.scrollLeft > 0;
  const showFixedRightShadow = hasHorizontalOverflow && hasFixedRight && horizontalScroll.scrollLeft < maxScrollLeft;

  /** 单个表头单元格：排序、问号提示与筛选入口都在这一层，分组标题与子列头共用。 */
  function renderHeadCell(column: TableColumn<Row>, rowSpan?: number) {
    const active = column.sortable && sortKey === column.key;
    const nextOrder: TableSortOrder = active && sortOrder === 'asc' ? 'desc' : 'asc';
    const selected = filterValues[column.key] ?? [];
    const label = typeof column.title === 'string' ? column.title : column.key;
    return <th className={column.className} data-align={column.align ?? 'left'} data-column-key={column.key} data-fixed={column.fixed} key={column.key} rowSpan={rowSpan} scope="col" style={columnStyle(column, fixedOffsets.get(column.key))}>
      <span className={styles.headContent}>
        {column.sortable
          ? <button aria-label={`按${label}排序`} className={styles.sortButton} onClick={() => onSortChange?.(column.key, nextOrder)} type="button">
            {column.title}
            <Icon className={styles.sortIcon} data-active={active || undefined} name={active && sortOrder === 'desc' ? 'a-arrow-down' : 'a-arrow-up'} size="xs" />
          </button>
          : column.title}
        {column.help ? <FieldHelp content={column.help} label={`${label}说明`} /> : null}
        {column.filterable
          ? <Popover align="end" content={<div className={styles.filterPanel}>
            {(column.filterOptions ?? []).map((option) => <Checkbox checked={selected.includes(option.value)} key={option.value} label={option.label} onCheckedChange={(checked) => onFilterChange?.(column.key, checked ? [...selected, option.value] : selected.filter((value) => value !== option.value))} value={option.value} />)}
            <div className={styles.filterActions}>
              <Button onClick={() => onFilterChange?.(column.key, [])} size="small" type="default">重置</Button>
            </div>
          </div>} title={`筛选${label}`}>
            <button aria-label={`筛选${label}`} className={styles.filterButton} data-active={selected.length > 0 || undefined} data-icon="filter" type="button">
              <Icon className={styles.filterIcon} name="filter" size="xs" />
            </button>
          </Popover>
          : null}
      </span>
    </th>;
  }

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <div className={styles.viewport} data-table-viewport>
        <div className={styles.scroll} onScroll={onScroll} onWheel={onWheel} ref={scrollRef} style={scroll?.y === undefined ? undefined : { maxHeight: `${scroll.y}px`, overflowY: 'auto' }}>
          <table
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        className={styles.table}
        data-bordered={bordered || undefined}
        data-clickable={onRowClick ? true : undefined}
        data-stripe={stripe || undefined}
        data-layout={tableLayout}
        data-sticky-header={stickyHeader || undefined}
        style={tableMinWidth === undefined ? undefined : { minWidth: `${tableMinWidth}px` }}
      >
        <thead>
          <tr>{selectable
            ? <th className={styles.optionCell} rowSpan={hasGroupedHeader ? 2 : undefined} scope="col">
              <Checkbox ariaLabel="全选当前页" checked={allSelected} indeterminate={selectedVisible.length > 0 && !allSelected} onCheckedChange={toggleAll} value="__all__" />
            </th>
            : null}{columns.map((column) => {
            /* 分组列只在第一行占一格，跨子列数；普通列在有两级表头时纵向占满两行。 */
            if (column.children?.length) {
              const label = typeof column.title === 'string' ? column.title : column.key;
              return <th className={`${styles.groupHeader} ${column.className ?? ''}`.trim()} colSpan={column.children.length} data-align="center" data-group-header key={column.key} scope="colgroup">
                <span className={styles.headContent}>
                  {column.title}
                  {column.help ? <FieldHelp content={column.help} label={`${label}说明`} /> : null}
                </span>
              </th>;
            }
            return renderHeadCell(column, hasGroupedHeader ? 2 : undefined);
          })}</tr>
          {hasGroupedHeader
            ? <tr>{columns.filter((column) => column.children?.length).flatMap((column) => column.children!).map((child) => renderHeadCell(child))}</tr>
            : null}
        </thead>
        <tbody>
          {visibleRows.length === 0
            ? <tr><td className={styles.empty} colSpan={leafColumns.length + (selectable ? 1 : 0)}>{emptyText}</td></tr>
            : visibleRows.map((row, rowIndex) => (
              <tr {...rowProps?.(row)} data-current={rowKey(row) === currentKey || undefined} key={rowKey(row)} onClick={() => { onCurrentChange?.(rowKey(row)); onRowClick?.(row); }}>
                {selectable
                  ? <td className={styles.optionCell}><Checkbox ariaLabel={`选择第 ${rowIndex + 1} 行`} checked={selectedKeys.includes(rowKey(row))} onCheckedChange={(checked) => toggleRow(rowKey(row), checked)} value={String(rowKey(row))} /></td>
                  : null}
                {leafColumns.map((column) => <td className={column.className} data-align={column.align ?? 'left'} data-column-key={column.key} data-fixed={column.fixed} key={column.key} style={columnStyle(column, fixedOffsets.get(column.key))}>{cellContent(column, row, rowIndex)}</td>)}
              </tr>
            ))}
        </tbody>
        {summary
          ? <tfoot><tr>{selectable ? <td className={styles.optionCell} /> : null}{summary(visibleRows).map((cell, index) => <td key={leafColumns[index]?.key ?? index}>{cell}</td>)}</tr></tfoot>
          : null}
          </table>
        </div>
        {showFixedLeftShadow
          ? <span aria-hidden="true" className={styles.fixedPane} data-fixed-pane="left" style={{ left: `${fixedLeftOffset}px` }} />
          : null}
        {showFixedRightShadow
          ? <span aria-hidden="true" className={styles.fixedPane} data-fixed-pane="right" style={{ right: `${fixedRightOffset}px` }} />
          : null}
      </div>
      {hasHorizontalOverflow ? <HorizontalScrollbar metrics={horizontalScroll} onScrollLeftChange={setScrollLeft} /> : null}
      {pagination && rows.length > 0
        ? <div className={styles.pagination} data-table-pagination>
          {pagination.footerStart ? <div className={styles.paginationStart}>{pagination.footerStart}</div> : null}
          <Pagination align={pagination.align} ariaLabel={pagination.ariaLabel ?? '表格分页'} disabled={pagination.disabled} hideOnSinglePage={pagination.hideOnSinglePage} itemRender={pagination.itemRender} onPageChange={pagination.onPageChange} onPageSizeChange={pagination.onPageSizeChange} onShowSizeChange={pagination.onShowSizeChange} page={visiblePage} pageSize={pagination.pageSize} pageSizeOptions={pagination.pageSizeOptions} showLessItems={pagination.showLessItems} showQuickJumper={pagination.showQuickJumper} showSizeChanger={pagination.showSizeChanger} showTitle={pagination.showTitle} showTotal={pagination.showTotal ?? (pagination.footerStart ? false : undefined)} simple={pagination.simple} size={pagination.size} total={rows.length} totalBoundaryShowSizeChanger={pagination.totalBoundaryShowSizeChanger} variant={pagination.variant} />
        </div>
        : null}
    </div>
  );
}
